import React, { useState } from "react"
import Button from "@mui/material/Button"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import FormLabel from "@mui/material/FormLabel"
import Radio from "@mui/material/Radio"
import RadioGroup from "@mui/material/RadioGroup"
import FormControlLabel from "@mui/material/FormControlLabel"
import Box from "@mui/material/Box"
import FormControl from "@mui/material/FormControl"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import InputLabel from "@mui/material/InputLabel"
import Checkbox from "@mui/material/Checkbox"
import FormGroup from "@mui/material/FormGroup"
import FormHelperText from "@mui/material/FormHelperText"
import InnerciseSelector from "components/common/InnerciseSelector/InnerciseSelector"
import { addStartResource, editStartResource } from "services/startHere"
import { verifyResponse } from "utils/httpRequest"
import { START_RESOURCE_DATA_TYPE, TIER_ACCESS_LIST } from "constants/global"
import { StartHereDialog } from "./StartHere.style"

const StartHereDlg = ({ onSubmit }) => {
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [startResource, setStartResource] = useState(null)
  const [resourceTitle, setResourceTitle] = useState("")
  const [resourceDescription, setResourceDescription] = useState("")
  const [resourceThumb, setResourceThumb] = useState("")

  const initStartResource = {
    dataType: START_RESOURCE_DATA_TYPE.INNERCISE,
    dataId: 0,
    isHome: 0,
    id: 0,
    tierAccess: "",
    tierExclusive: false,
  }

  const [
    openInnerciseSelectorDlg,
    innerciseSelectorDlg,
    innerciseSelectorLoading,
  ] = InnerciseSelector({
    onSelect: (selectedInnercise) => {
      setStartResource({
        ...startResource,
        dataId: selectedInnercise.id,
      })
      setResourceTitle(selectedInnercise.title)
      setResourceDescription(selectedInnercise.description)
      setResourceThumb(
        selectedInnercise.thumbResourceDto.urlDto.imgUrl ||
          selectedInnercise.thumbResourceDto.urlDto.originalUrl ||
          ""
      )
    },
  })

  const handleClose = () => {
    setOpen(false)
  }

  const addNewStartResource = async () => {
    setLoading(true)
    const response = await addStartResource(startResource)
    setLoading(false)
    verifyResponse(response)

    onSubmit && onSubmit()

    setOpen(false)
  }

  const editNewStartResource = async () => {
    setLoading(true)
    const response = await editStartResource(startResource)
    setLoading(false)
    verifyResponse(response)
    onSubmit && onSubmit()
    setOpen(false)
  }

  const openDlg = (editStartResource) => {
    // reset();
    if (editStartResource) {
      setResourceTitle(editStartResource.innerciseDto.title)
      setResourceDescription(editStartResource.innerciseDto.description)
      setResourceThumb(
        editStartResource.innerciseDto.thumbResourceDto.urlDto.imgUrl ||
          editStartResource.innerciseDto.thumbResourceDto.urlDto.originalUrl ||
          ""
      )
    }

    setStartResource(
      editStartResource
        ? {
            ...startResource,
            dataType: START_RESOURCE_DATA_TYPE.INNERCISE,
            dataId: editStartResource.dataId,
            isHome: editStartResource.isHome,
            id: editStartResource.id,
            tierAccess: editStartResource.tierAccess || "",
            tierExclusive: Boolean(editStartResource.tierExclusive),
          }
        : initStartResource
    )
    setIsEditMode(editStartResource ? true : false)
    setOpen(true)
  }

  const dlg = (
    <StartHereDialog open={open} onClose={handleClose}>
      <DialogTitle>
        <div className="dialog-title-group">
          <h2 className="dialog-title">
            {isEditMode ? "Edit Start Resource" : "Add New Start Resource"}
          </h2>
          <p className="dialog-subtitle">
            Choose the innercise and configure display behavior for onboarding.
          </p>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="actions">
          <Button
            variant="outlined"
            size="medium"
            onClick={() => {
              openInnerciseSelectorDlg()
            }}
          >
            Select Innercise
          </Button>
        </div>

        <Box>
          <div className="innercise">
            {startResource && startResource.dataId !== 0 ? (
              <div className="innercise-content">
                <img
                  className="thumb"
                  src={resourceThumb}
                  alt="resource thumb"
                />
                <div className="info">Title: {resourceTitle}</div>
                <p className="info">Description: {resourceDescription}</p>
              </div>
            ) : (
              <div className="no-selection">No Resource Selected Yet...</div>
            )}
          </div>
        </Box>

        <FormControl>
          <FormLabel id="radio-buttons-is-home-group-label">Is Home</FormLabel>
          <RadioGroup
            aria-labelledby="radio-buttons-is-home-group-label"
            value={startResource ? startResource.isHome : 0}
            name="radio-buttons-is-home"
            onChange={(e) => {
              setStartResource({
                ...startResource,
                isHome: e.target.value,
              })
            }}
          >
            <FormControlLabel value="1" control={<Radio />} label="Yes" />
            <FormControlLabel value="0" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>

        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="start-here-tier-access-label">Tier / Access (optional)</InputLabel>
            <Select
              labelId="start-here-tier-access-label"
              value={startResource ? (startResource.tierAccess || "") : ""}
              label="Tier / Access (optional)"
              onChange={(e) => {
                setStartResource({
                  ...startResource,
                  tierAccess: e.target.value,
                })
              }}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {/* Membership tiers */}
              {Array.isArray(TIER_ACCESS_LIST) &&
                TIER_ACCESS_LIST.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.text}
                  </MenuItem>
                ))}
              {/* Product gate */}
              <MenuItem value="product">Product</MenuItem>
            </Select>
            <FormHelperText>
              Leave as “All” to show for everyone. If you choose a tier, you can optionally make it exclusive to that tier.
            </FormHelperText>
          </FormControl>
        </Box>

        {/* Exclusive to tier: only applies to membership tiers */}
        <Box sx={{ mt: 1 }}>
          <FormControl component="fieldset" variant="standard">
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(startResource?.tierExclusive)}
                    onChange={(e) => {
                      setStartResource({
                        ...startResource,
                        tierExclusive: e.target.checked,
                      })
                    }}
                    disabled={!startResource?.tierAccess || String(startResource?.tierAccess) === "product"}
                  />
                }
                label="Exclusive to tier (only show to that exact tier)"
              />
            </FormGroup>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          className="dialog-cancel-btn"
          onClick={handleClose}
          variant="outlined"
          size="medium"
        >
          Close
        </Button>
        <Button
          className="dialog-submit-btn"
          type="submit"
          variant="contained"
          size="medium"
          disabled={startResource && startResource.dataId === 0}
          onClick={() => {
            isEditMode ? editNewStartResource() : addNewStartResource()
          }}
        >
          {isEditMode ? "Update" : "Create"}
        </Button>
      </DialogActions>
      {innerciseSelectorDlg}
    </StartHereDialog>
  )

  return [openDlg, dlg, loading || innerciseSelectorLoading]
}

export default StartHereDlg
